import { useSyncExternalStore } from 'react';

/**
 * DEV-555 — estado da confirmação de identidade (modal) do App Candidato.
 *
 * Store externa (sem provider) para o gate ser acionável de qualquer tela e o
 * modal viver uma vez só, no root do router (`IdentityConfirmationHost`).
 *
 * O que vive aqui é SÓ memória: um reload com a confirmação aberta descarta a
 * ação pendente (ACC-040) — a pessoa recomeça pelo gatilho. A ida ao provedor
 * social é a única exceção, e ela usa `sessionStorage` em
 * `identity-reauth-return.ts`, não esta store.
 */
export type ProfileEditTarget = 'personal-data' | 'email' | 'password' | 'photo';

export type PendingAction =
  | { kind: 'profile-edit'; target: ProfileEditTarget }
  | { kind: 'withdraw'; jobId: string }
  | { kind: 'delete-account' };

export type ProviderError = 'different-account' | null;

export interface IdentityConfirmationRequest {
  action: PendingAction;
  /** Caminho (pathname + search) da página de origem, para voltar após o provedor. */
  originPath: string;
  /** Executa a ação que abriu a confirmação (só no fluxo sem reload). */
  onConfirmed?: () => void;
}

export interface IdentityConfirmationSnapshot {
  open: boolean;
  request: IdentityConfirmationRequest | null;
  providerError: ProviderError;
  /** Ação cuja confirmação acabou de concluir via provedor (consumida pela página de origem). */
  completed: { action: PendingAction; at: number } | null;
}

const listeners = new Set<() => void>();
let snapshot: IdentityConfirmationSnapshot = {
  open: false,
  request: null,
  providerError: null,
  completed: null,
};

function emit(next: Partial<IdentityConfirmationSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  for (const l of [...listeners]) l();
}

export function openIdentityConfirmation(
  request: IdentityConfirmationRequest,
  opts: { providerError?: ProviderError } = {}
): void {
  emit({ open: true, request, providerError: opts.providerError ?? null });
}

/** Dispensa (alça, Dialog Close): volta à origem com a sessão intacta; a ação NÃO executa (AC-34). */
export function closeIdentityConfirmation(): void {
  emit({ open: false, request: null, providerError: null });
}

export function setIdentityProviderError(error: ProviderError): void {
  emit({ providerError: error });
}

/**
 * Sucesso: fecha a confirmação e executa a ação que a abriu (AC-22/AC-30).
 * Sem `onConfirmed` (retorno do provedor após reload), registra `completed`
 * para a página de origem consumir ao montar.
 */
export function markIdentityConfirmed(): PendingAction | null {
  const request = snapshot.request;
  emit({
    open: false,
    request: null,
    providerError: null,
    completed: request && !request.onConfirmed ? { action: request.action, at: Date.now() } : null,
  });
  if (request?.onConfirmed) request.onConfirmed();
  return request?.action ?? null;
}

/** Página de origem consome a ação concluída via provedor (uma vez). */
export function consumeCompletedAction<K extends PendingAction['kind']>(
  kind: K
): Extract<PendingAction, { kind: K }> | null {
  const completed = snapshot.completed;
  if (!completed || completed.action.kind !== kind) return null;
  emit({ completed: null });
  return completed.action as Extract<PendingAction, { kind: K }>;
}

export function getIdentityConfirmationSnapshot(): IdentityConfirmationSnapshot {
  return snapshot;
}

export function subscribeIdentityConfirmation(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useIdentityConfirmationStore(): IdentityConfirmationSnapshot {
  return useSyncExternalStore(
    subscribeIdentityConfirmation,
    getIdentityConfirmationSnapshot,
    getIdentityConfirmationSnapshot
  );
}

/** Só para testes. */
export function resetIdentityConfirmationStore(): void {
  emit({ open: false, request: null, providerError: null, completed: null });
}
